import {
  Box,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { money } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import {
  balanceTone,
  presetRange,
  RANGE_PRESETS,
  type PartyBalance,
  type RangePreset,
  type Tone,
} from './model';

const TONE_COLOR: Record<Tone, string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  info: 'info.main',
  default: 'text.secondary',
};

/** A summary tile for detail-page headers: label, big tabular value, hint, tinted icon. */
export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'default',
  loading,
  children,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon: ReactNode;
  tone?: Tone;
  loading?: boolean;
  children?: ReactNode;
}) {
  return (
    <Card sx={{ position: 'relative', overflow: 'hidden', height: '100%' }}>
      <Box
        aria-hidden
        sx={(t) => {
          const key = tone === 'default' ? 'primary' : tone;
          const c = (t.vars ?? t).palette[key].main;
          return {
            position: 'absolute',
            right: -36,
            top: -36,
            width: 128,
            height: 128,
            borderRadius: '50%',
            background: `radial-gradient(circle, color-mix(in srgb, ${c} 16%, transparent), transparent 70%)`,
          };
        }}
      />
      <CardContent>
        <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550 }}>
            {label}
          </Typography>
          <Box
            sx={(t) => {
              const key = tone === 'default' ? 'primary' : tone;
              const c = (t.vars ?? t).palette[key].main;
              return {
                width: 36,
                height: 36,
                borderRadius: '11px',
                display: 'grid',
                placeItems: 'center',
                color: c,
                bgcolor: `color-mix(in srgb, ${c} 10%, transparent)`,
                '& svg': { fontSize: 19 },
              };
            }}
          >
            {icon}
          </Box>
        </Stack>
        {loading ? (
          <Skeleton width="60%" height={40} />
        ) : (
          <Typography variant="h4" className="tabular" sx={{ mt: 0.5, fontSize: '1.375rem' }}>
            {value}
          </Typography>
        )}
        {hint !== undefined && (
          <Typography
            variant="caption"
            sx={{ color: TONE_COLOR[tone], fontWeight: 550, display: 'block', mt: 0.25 }}
          >
            {loading ? <Skeleton width="40%" /> : hint}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

/** A balance as amount plus its meaning ("Customer owes shop"), coloured by direction. */
export function BalanceText({
  balance,
  align = 'right',
  dense,
}: {
  balance: PartyBalance;
  align?: 'left' | 'right';
  dense?: boolean;
}) {
  const { format } = useShop();
  const tone = balanceTone(balance);
  return (
    <Box sx={{ textAlign: align }}>
      <Typography
        component="div"
        variant="body2"
        className="tabular"
        sx={{ fontWeight: 650, whiteSpace: 'nowrap' }}
      >
        {balance.direction === 'SETTLED' ? money(0, format) : money(balance.amount, format)}
      </Typography>
      {!dense && (
        <Typography
          component="div"
          variant="caption"
          sx={{ color: TONE_COLOR[tone], fontWeight: 550, whiteSpace: 'nowrap' }}
        >
          {balance.label}
        </Typography>
      )}
    </Box>
  );
}

/** Role chips for a contact that can be a customer, a supplier or both. */
export function RoleChips({
  isCustomer,
  isSupplier,
  isActive = true,
  isWalkIn,
}: {
  isCustomer: boolean;
  isSupplier: boolean;
  isActive?: boolean;
  isWalkIn?: boolean;
}) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
      {isCustomer && <Chip size="small" label="Customer" color="primary" variant="outlined" />}
      {isSupplier && <Chip size="small" label="Supplier" color="info" variant="outlined" />}
      {isWalkIn && <Chip size="small" label="Walk-in" variant="outlined" />}
      {!isActive && <Chip size="small" label="Inactive" />}
    </Stack>
  );
}

/** From/to date pickers with quick presets. Empty `from` means "from the beginning". */
export function DateRangeBar({
  from,
  to,
  today,
  onChange,
  children,
}: {
  from: string;
  to: string;
  today: string;
  onChange: (range: { from: string; to: string }) => void;
  children?: ReactNode;
}) {
  const preset =
    RANGE_PRESETS.find((p) => {
      const r = presetRange(p.value, today);
      return r.from === from && r.to === to;
    })?.value ?? 'custom';
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap', rowGap: 1.5 }}
    >
      <TextField
        select
        label="Period"
        value={preset}
        onChange={(e) => {
          if (e.target.value !== 'custom')
            onChange(presetRange(e.target.value as RangePreset, today));
        }}
        sx={{ width: { sm: 170 } }}
      >
        {RANGE_PRESETS.map((p) => (
          <MenuItem key={p.value} value={p.value}>
            {p.label}
          </MenuItem>
        ))}
        <MenuItem value="custom" disabled>
          Custom
        </MenuItem>
      </TextField>
      <Stack direction="row" spacing={1.5}>
        <TextField
          type="date"
          label="From"
          value={from}
          onChange={(e) => onChange({ from: e.target.value, to })}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to || today } }}
          sx={{ width: { sm: 168 } }}
        />
        <TextField
          type="date"
          label="To"
          value={to}
          onChange={(e) => onChange({ from, to: e.target.value || today })}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from, max: today } }}
          sx={{ width: { sm: 168 } }}
        />
      </Stack>
      {children}
    </Stack>
  );
}

/** Small label/value pair for detail panels. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: 'block', lineHeight: 1.6 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 550, wordBreak: 'break-word' }} component="div">
        {children}
      </Typography>
    </Box>
  );
}
